import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  getAppointments,
  getConvenios,
  NO_APLICA_CONVENIO_FILTER,
  updateAppointment,
  markAppointmentNoShow,
  getAvailableSlots,
  getAdminAppointmentTypes,
  createAdminAppointment,
  deleteAdminAppointment,
  getNextConsultorio,
} from '../lib/api';
import { toast } from 'sonner';
import {
  Clock,
  Calendar as CalendarIconLucide,
  User,
  Phone,
  Mail,
  Plus,
  FileText,
  UserX,
  Trash2,
  Search,
} from 'lucide-react';
import { es } from 'date-fns/locale';
import { useMexicoCityClock } from '../hooks/useMexicoCityClock';
import {
  calendarDayKeyInClinic,
  compareAppointmentsByClinicDateTime,
  formatAppointmentDateOnlyInClinic,
  todayYmdInClinic,
  addCalendarDaysYmd,
  formatYmdClinic,
  ymdToDatePickerDate,
  calendarIsoWeekdayFromYmd,
} from '../lib/clinicTime';
import ClinicalAppointmentDialog from '../components/clinical/ClinicalAppointmentDialog';
import {
  appointmentRowHighlightClass,
  hasNoShowBookingWarning,
  needsAttendanceConfirmation,
} from '../lib/appointmentBookingHighlight';
import AppointmentBookingLegend from '../components/AppointmentBookingLegend';
import AppointmentWeekDayPicker from '../components/AppointmentWeekDayPicker';
import ConsultorioOccupancyGrid from '../components/ConsultorioOccupancyGrid';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { formatRoomUnit, roomLabelOf } from '../lib/roomLabel';

const initialNewAppointmentForm = {
  patient_name: '',
  patient_age: '',
  patient_email: '',
  patient_phone: '',
  reason: '',
  notes: '',
};

function appointmentDayKeyInClinic(appointment) {
  return calendarDayKeyInClinic(appointment?.date);
}

function summarizeAppointmentsByStatus(rows) {
  const counts = {
    total: rows.length,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0,
  };
  for (const appt of rows) {
    const st = String(appt?.status || 'pending').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(counts, st)) {
      counts[st] += 1;
    }
  }
  return counts;
}

/** Nombre libre: al menos 2 caracteres (apellidos opcionales). */
const validatePatientName = (name) => String(name || '').trim().length >= 2;

const parsePatientAgeYears = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 120) return null;
  return n;
};

const LIST_ARCHIVED_STATUSES = new Set(['cancelled', 'completed', 'no_show']);

function defaultListDateRange() {
  const today = todayYmdInClinic();
  const [y, m] = today.split('-').map(Number);
  const date_from = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const date_to = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { date_from, date_to };
}

export default function AdminAppointments() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convenioFilter, setConvenioFilter] = useState('all');
  const [listPatientSearch, setListPatientSearch] = useState('');
  const [listPatientSearchDebounced, setListPatientSearchDebounced] = useState('');
  const [listDateRange, setListDateRange] = useState(defaultListDateRange);
  const [convenios, setConvenios] = useState([]);
  const [selectedDateYmd, setSelectedDateYmd] = useState(() => todayYmdInClinic());

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [typeSchedule, setTypeSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedConsultorio, setSelectedConsultorio] = useState(1);
  const [selectedTypeRoomCount, setSelectedTypeRoomCount] = useState(1);
  const [selectedTypeRoomLabel, setSelectedTypeRoomLabel] = useState('Consultorio');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsAvailable, setSlotsAvailable] = useState(true);
  const [slotsReason, setSlotsReason] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newApptForm, setNewApptForm] = useState(initialNewAppointmentForm);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({ id: null, patientName: '' });
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState({ id: null, patientName: '' });
  const [noShowSubmitting, setNoShowSubmitting] = useState(false);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState(null);
  const [gridRefreshToken, setGridRefreshToken] = useState(0);

  const [clinicalDialog, setClinicalDialog] = useState({
    open: false,
    appointmentId: null,
    patientName: '',
  });

  const mxClock = useMexicoCityClock();

  const refreshOccupancyGrid = useCallback(() => {
    setGridRefreshToken((t) => t + 1);
  }, []);

  const sortedAppointments = useMemo(
    () => [...appointments].sort(compareAppointmentsByClinicDateTime),
    [appointments],
  );

  const canDeleteAppointments = isSuperAdmin(user?.role);

  const listSearchActive = Boolean(String(listPatientSearchDebounced || '').trim());

  const listFilteredAppointments = useMemo(() => {
    if (listSearchActive) return sortedAppointments;
    return sortedAppointments.filter((appt) =>
      LIST_ARCHIVED_STATUSES.has(String(appt?.status || '').toLowerCase()),
    );
  }, [sortedAppointments, listSearchActive]);

  const listStatusSummary = useMemo(
    () => summarizeAppointmentsByStatus(listFilteredAppointments),
    [listFilteredAppointments],
  );

  const listDateFilterLabel = `${formatYmdClinic(listDateRange.date_from, 'd MMM yyyy')} — ${formatYmdClinic(listDateRange.date_to, 'd MMM yyyy')}`;

  const loadConvenios = useCallback(async () => {
    try {
      const data = await getConvenios(false);
      setConvenios(Array.isArray(data) ? data : []);
    } catch {
      /* opcional en listado */
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    try {
      const search = String(listPatientSearchDebounced || '').trim();
      const data = await getAppointments({
        convenio_id: convenioFilter === 'all' ? null : convenioFilter,
        ...(search
          ? { patient_search: search }
          : {
              date_from: listDateRange.date_from,
              date_to: listDateRange.date_to,
            }),
      });
      setAppointments(data);
    } catch (error) {
      toast.error('Error al cargar citas');
    } finally {
      setLoading(false);
    }
  }, [convenioFilter, listDateRange, listPatientSearchDebounced]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setListPatientSearchDebounced(String(listPatientSearch || '').trim());
    }, 450);
    return () => window.clearTimeout(handle);
  }, [listPatientSearch]);

  useEffect(() => {
    loadConvenios();
  }, [loadConvenios]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (!newDialogOpen) return;
    const loadTypes = async () => {
      try {
        const data = await getAdminAppointmentTypes();
        const enabled = (Array.isArray(data) ? data : []).filter((t) => t.enabled);
        setAppointmentTypes(enabled);
        if (enabled.length > 0) {
          setSelectedTypeId((prev) => prev || enabled[0].id);
        }
      } catch {
        toast.error('No se pudieron cargar los tipos de cita');
      }
    };
    loadTypes();
  }, [newDialogOpen]);

  useEffect(() => {
    if (!selectedTypeId) {
      setTypeSchedule(null);
      setSelectedTypeRoomCount(1);
      setSelectedTypeRoomLabel('Consultorio');
      return;
    }
    const t = appointmentTypes.find((x) => x.id === selectedTypeId);
    setTypeSchedule(t?.schedule || null);
    setSelectedTypeRoomCount(Math.max(1, Number(t?.room_count) || 1));
    setSelectedTypeRoomLabel(roomLabelOf(t));
  }, [selectedTypeId, appointmentTypes]);

  useEffect(() => {
    if (!newDialogOpen || !selectedDate || !selectedTypeId) return;
    const loadSlots = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = selectedDate ? calendarDayKeyInClinic(selectedDate) : '';
        if (!dateStr) return;
        const data = await getAvailableSlots(dateStr, { typeId: selectedTypeId });
        setAvailableSlots(data.slots || []);
        setSlotsAvailable(data.available);
        setSlotsReason(data.reason || '');
      } catch {
        setAvailableSlots([]);
        toast.error('Error al cargar horarios disponibles');
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [newDialogOpen, selectedDate, selectedTypeId]);

  useEffect(() => {
    if (!newDialogOpen || !selectedTypeId || !selectedDate || !selectedTime) return;
    const roomCount = Math.max(
      1,
      Number(appointmentTypes.find((x) => x.id === selectedTypeId)?.room_count) || selectedTypeRoomCount || 1,
    );
    if (roomCount <= 1) {
      setSelectedConsultorio(1);
      return;
    }
    const dateStr = calendarDayKeyInClinic(selectedDate);
    if (!dateStr) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getNextConsultorio(selectedTypeId, dateStr, selectedTime);
        if (!cancelled) {
          setSelectedConsultorio(data.consultorio_number || 1);
          if (data.room_label) setSelectedTypeRoomLabel(roomLabelOf(data.room_label));
        }
      } catch {
        if (!cancelled) setSelectedConsultorio(1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    newDialogOpen,
    selectedTypeId,
    selectedDate,
    selectedTime,
    appointmentTypes,
    selectedTypeRoomCount,
  ]);

  const resetNewAppointmentDialog = () => {
    setSelectedTypeId('');
    setTypeSchedule(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedConsultorio(1);
    setSelectedTypeRoomCount(1);
    setSelectedTypeRoomLabel('Consultorio');
    setAvailableSlots([]);
    setSlotsAvailable(true);
    setSlotsReason('');
    setNewApptForm(initialNewAppointmentForm);
  };

  const isDateDisabled = (date) => {
    const ymd = calendarDayKeyInClinic(date);
    if (!ymd) return true;
    if (ymd < todayYmdInClinic()) return true;
    if (!typeSchedule) return false;
    const isoDay = calendarIsoWeekdayFromYmd(ymd);
    if (!Number.isFinite(isoDay)) return true;
    return !typeSchedule.working_days.includes(isoDay);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!selectedTypeId || !selectedDate || !selectedTime) {
      toast.error('Selecciona tipo, fecha y hora');
      return;
    }
    if (selectedTypeRoomCount > 1 && !selectedConsultorio) {
      toast.error(`Asigna un ${selectedTypeRoomLabel.toLowerCase()} para este servicio`);
      return;
    }
    if (!validatePatientName(newApptForm.patient_name)) {
      toast.error('Indica el nombre del paciente (al menos 2 caracteres).');
      return;
    }
    const ageRaw = String(newApptForm.patient_age ?? '').trim();
    const ageYears = ageRaw ? parsePatientAgeYears(newApptForm.patient_age) : null;
    if (ageRaw && ageYears == null) {
      toast.error('Si indicas edad, debe ser entre 1 y 120 años.');
      return;
    }
    setCreating(true);
    try {
      const emailTrim = newApptForm.patient_email.trim();
      const payload = {
        patient_name: newApptForm.patient_name.trim(),
        patient_phone: newApptForm.patient_phone,
        reason: newApptForm.reason,
        notes: newApptForm.notes,
        date: calendarDayKeyInClinic(selectedDate) || '',
        time: selectedTime,
        appointment_type_id: selectedTypeId,
      };
      if (ageYears != null) payload.patient_age = ageYears;
      if (emailTrim) payload.patient_email = emailTrim;
      if (selectedTypeRoomCount > 1) {
        payload.consultorio_number = Number(selectedConsultorio);
      }
      await createAdminAppointment(payload);
      toast.success('Cita creada');
      setNewDialogOpen(false);
      resetNewAppointmentDialog();
      loadAppointments();
      refreshOccupancyGrid();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cita';
      toast.error(typeof message === 'string' ? message : 'Error al crear la cita');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateAppointment(id, { status: newStatus });
      const msg =
        newStatus === 'confirmed'
          ? 'Cita confirmada'
          : newStatus === 'completed'
            ? 'Cita marcada como completada'
            : newStatus === 'cancelled'
              ? 'Cita cancelada'
              : 'Cita actualizada';
      toast.success(msg);
      loadAppointments();
      refreshOccupancyGrid();
    } catch (error) {
      toast.error('Error al actualizar cita');
    }
  };

  const handleDeleteAppointment = async (appointment) => {
    const appointmentId = String(appointment?.id || '').trim();
    if (!appointmentId) return;
    const patientName = String(appointment?.patient_name || '').trim();
    const dateLabel = formatAppointmentDateOnlyInClinic(appointment?.date, 'EEE, d MMM yyyy');
    const timeLabel = String(appointment?.time || '').trim();
    const confirmed = window.confirm(
      `Se borrará definitivamente la cita de ${patientName || 'paciente'} (${dateLabel} ${timeLabel}). Esta acción no se puede deshacer y no enviará notificación al usuario.\n\n¿Deseas continuar?`
    );
    if (!confirmed) return;
    setDeletingAppointmentId(appointmentId);
    try {
      await deleteAdminAppointment(appointmentId);
      toast.success('Cita borrada');
      loadAppointments();
      refreshOccupancyGrid();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo borrar la cita');
    } finally {
      setDeletingAppointmentId(null);
    }
  };

  const openCancelDialog = (appointmentId, patientName = '') => {
    setCancelTarget({ id: appointmentId, patientName });
    const first = patientName ? String(patientName).trim().split(/\s+/)[0] : '';
    setCancelMessage(
      `Hola${first ? ` ${first}` : ''}, lamentamos informarte que tu cita en Centro Cultural quedó cancelada. Si deseas otra fecha, escríbenos.`,
    );
    setCancelDialogOpen(true);
  };

  const openNoShowDialog = (appointmentId, patientName = '') => {
    setNoShowTarget({
      id: appointmentId,
      patientName: String(patientName || '').trim(),
    });
    setNoShowDialogOpen(true);
  };

  const submitMarkNoShow = async () => {
    if (!noShowTarget.id) return;
    setNoShowSubmitting(true);
    try {
      const data = await markAppointmentNoShow(noShowTarget.id);
      const count = data?.booking_no_show_count;
      let msg = 'Inasistencia registrada';
      if (typeof count === 'number' && count > 0) {
        msg += `. Inasistencias acumuladas para este teléfono: ${count}`;
      }
      toast.success(msg);
      setNoShowDialogOpen(false);
      setNoShowTarget({ id: null, patientName: '' });
      loadAppointments();
      refreshOccupancyGrid();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo registrar la inasistencia');
    } finally {
      setNoShowSubmitting(false);
    }
  };

  const submitCancelAppointment = async () => {
    if (!cancelTarget.id) return;
    setCancelSubmitting(true);
    try {
      await updateAppointment(cancelTarget.id, {
        status: 'cancelled',
        cancel_whatsapp_message: cancelMessage.trim(),
      });
      toast.success('Cita cancelada');
      setCancelDialogOpen(false);
      setCancelTarget({ id: null, patientName: '' });
      loadAppointments();
      refreshOccupancyGrid();
    } catch (error) {
      toast.error('Error al cancelar la cita');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      confirmed: 'bg-success/10 text-success border-success/20',
      cancelled: 'bg-accent/10 text-accent border-accent/20',
      completed: 'bg-primary/10 text-primary border-primary/20',
      no_show: 'bg-amber-500/15 text-amber-800 border-amber-500/30 dark:text-amber-200',
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Completada',
      no_show: 'No llegó',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando citas...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-appointments">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>Citas</span>
            <span
              className="text-sm sm:text-base font-mono font-semibold text-muted-foreground tracking-tight"
              title="Fecha y hora en Ciudad de México (centro), actualización cada segundo"
            >
              {mxClock.fullLabel}
            </span>
          </h1>
          <p className="text-muted-foreground">Gestiona las citas del consultorio</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Dialog
            open={newDialogOpen}
            onOpenChange={(open) => {
              setNewDialogOpen(open);
              if (!open) resetNewAppointmentDialog();
            }}
          >
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2" data-testid="admin-new-appointment-btn">
                <Plus className="w-4 h-4" />
                Nueva cita
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Crear cita (administrador)</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Tipo de servicio</Label>
                <Select
                  value={selectedTypeId}
                  onValueChange={(id) => {
                    setSelectedTypeId(id);
                    setSelectedDate(null);
                    setSelectedTime(null);
                    setSelectedConsultorio(1);
                    setAvailableSlots([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Elige el tipo de cita" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.label} ({t.room_count}{' '}
                        {Number(t.room_count) === 1
                          ? roomLabelOf(t).toLowerCase()
                          : `${roomLabelOf(t).toLowerCase()}s`}
                        )
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CalendarIconLucide className="w-4 h-4 text-primary" />
                      Fecha
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => {
                        setSelectedDate(d);
                        setSelectedTime(null);
                        setSelectedConsultorio(1);
                      }}
                      disabled={isDateDisabled}
                      fromDate={ymdToDatePickerDate(todayYmdInClinic())}
                      toDate={ymdToDatePickerDate(addCalendarDaysYmd(todayYmdInClinic(), 60))}
                      locale={es}
                      className="rounded-xl border mx-auto"
                      data-testid="admin-new-appt-calendar"
                    />
                  </CardContent>
                </Card>
                <Card className="border border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Hora
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedDate ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Elige una fecha</p>
                    ) : loadingSlots ? (
                      <p className="text-muted-foreground text-sm text-center py-6 animate-pulse">Cargando…</p>
                    ) : !slotsAvailable ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        <p>No hay horarios</p>
                        {slotsReason ? <p className="mt-1">{slotsReason}</p> : null}
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Sin cupos este día</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
                        {availableSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedTime === time
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-sage-50 text-foreground hover:bg-sage-100'
                            }`}
                            data-testid={`admin-new-appt-slot-${time}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedTime && selectedTypeRoomCount > 1 ? (
                      <div className="mt-4 space-y-2 border-t border-border pt-3">
                        <Label htmlFor="admin-new-consultorio">{selectedTypeRoomLabel} *</Label>
                        <select
                          id="admin-new-consultorio"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={selectedConsultorio}
                          onChange={(e) => setSelectedConsultorio(Number(e.target.value))}
                          data-testid="admin-new-appt-consultorio"
                        >
                          {Array.from({ length: selectedTypeRoomCount }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n}>
                              {formatRoomUnit(selectedTypeRoomLabel, n)}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Este servicio tiene {selectedTypeRoomCount} {selectedTypeRoomLabel.toLowerCase()}
                          s; elige cuál usar en este horario.
                        </p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
              <form onSubmit={handleCreateAppointment} className="space-y-3 mt-4" data-testid="admin-new-appointment-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="admin-appt-name" className="flex items-center gap-1">
                      <User className="w-3 h-3" /> Nombre *
                    </Label>
                    <Input
                      id="admin-appt-name"
                      value={newApptForm.patient_name}
                      onChange={(e) => setNewApptForm({ ...newApptForm, patient_name: e.target.value })}
                      required
                      placeholder="Ej. Ana López"
                      className="mt-1 h-10"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Basta con el nombre; los apellidos son opcionales.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="admin-appt-phone" className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Teléfono *
                    </Label>
                    <Input
                      id="admin-appt-phone"
                      value={newApptForm.patient_phone}
                      onChange={(e) => setNewApptForm({ ...newApptForm, patient_phone: e.target.value })}
                      required
                      className="mt-1 h-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="admin-appt-age">Edad (años, opcional)</Label>
                  <Input
                    id="admin-appt-age"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={120}
                    value={newApptForm.patient_age}
                    onChange={(e) => setNewApptForm({ ...newApptForm, patient_age: e.target.value })}
                    className="mt-1 h-10 max-w-[140px]"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-appt-email" className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email (opcional)
                  </Label>
                  <Input
                    id="admin-appt-email"
                    type="email"
                    value={newApptForm.patient_email}
                    onChange={(e) => setNewApptForm({ ...newApptForm, patient_email: e.target.value })}
                    className="mt-1 h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-appt-reason" className="flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Motivo
                  </Label>
                  <Textarea
                    id="admin-appt-reason"
                    value={newApptForm.reason}
                    onChange={(e) => setNewApptForm({ ...newApptForm, reason: e.target.value })}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={creating}>
                  {creating ? 'Guardando…' : 'Crear cita'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-6">
        <AppointmentBookingLegend title="Leyenda (citas pendientes)" />
      </div>

      <Card className="border border-border bg-sky-50/40 dark:bg-sky-950/20 mb-6">
        <CardContent className="pt-6">
          <AppointmentWeekDayPicker selectedYmd={selectedDateYmd} onSelectYmd={setSelectedDateYmd} />
        </CardContent>
      </Card>

      <div className="mb-6">
        <ConsultorioOccupancyGrid
          dateYmd={selectedDateYmd}
          refreshToken={gridRefreshToken}
          onConfirmAppointment={(id) => handleStatusChange(id, 'confirmed')}
          onCancelAppointment={openCancelDialog}
          onCompleteAppointment={(id) => handleStatusChange(id, 'completed')}
          onOpenClinicalHistory={(appointmentId, patientName) =>
            setClinicalDialog({
              open: true,
              appointmentId,
              patientName,
            })
          }
          onNoShowAppointment={openNoShowDialog}
        />
      </div>

      <Card className="border border-border">
        <CardHeader className="pb-3 space-y-4">
          <div>
            <CardTitle className="font-heading text-xl">Listado de citas</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {listSearchActive
                ? `Búsqueda: «${listPatientSearchDebounced}» · historial completo (todos los estados)`
                : `${listDateFilterLabel} · canceladas, atendidas o inasistencia`}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[220px] flex-1 max-w-md">
              <Label htmlFor="list-patient-search" className="text-xs">
                Buscar paciente
              </Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="list-patient-search"
                  type="search"
                  className="pl-9 h-9"
                  placeholder="Nombre o teléfono (historial completo)"
                  value={listPatientSearch}
                  onChange={(e) => setListPatientSearch(e.target.value)}
                  data-testid="list-patient-search"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="list-date-from" className="text-xs">
                Fecha inicio
              </Label>
              <Input
                id="list-date-from"
                type="date"
                className="mt-1 w-40 h-9"
                value={listDateRange.date_from}
                disabled={listSearchActive}
                onChange={(e) => setListDateRange((p) => ({ ...p, date_from: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="list-date-to" className="text-xs">
                Fecha fin
              </Label>
              <Input
                id="list-date-to"
                type="date"
                className="mt-1 w-40 h-9"
                value={listDateRange.date_to}
                disabled={listSearchActive}
                onChange={(e) => setListDateRange((p) => ({ ...p, date_to: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Convenio</Label>
              <Select value={convenioFilter} onValueChange={setConvenioFilter}>
                <SelectTrigger className="mt-1 w-48 h-9">
                  <SelectValue placeholder="Convenio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value={NO_APLICA_CONVENIO_FILTER}>No aplica</SelectItem>
                  {convenios
                    .filter((c) => !c.is_system_default)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <span className="font-medium text-foreground">
              {listStatusSummary.total} cita{listStatusSummary.total !== 1 ? 's' : ''}
            </span>
            <span className="text-muted-foreground">· ordenadas por fecha y hora</span>
            {listStatusSummary.total > 0 ? (
              <>
                <span className="text-muted-foreground hidden sm:inline">·</span>
                {listSearchActive ? (
                  <>
                    <Badge variant="outline" className="font-normal bg-warning/10 text-warning border-warning/20">
                      Pendientes: {listStatusSummary.pending}
                    </Badge>
                    <Badge variant="outline" className="font-normal bg-primary/10 text-primary border-primary/20">
                      Confirmadas: {listStatusSummary.confirmed}
                    </Badge>
                  </>
                ) : null}
                <Badge variant="outline" className="font-normal bg-success/10 text-success border-success/20">
                  Completadas: {listStatusSummary.completed}
                </Badge>
                <Badge variant="outline" className="font-normal bg-accent/10 text-accent border-accent/20">
                  Canceladas: {listStatusSummary.cancelled}
                </Badge>
                <Badge variant="outline" className="font-normal bg-amber-500/15 text-amber-900 border-amber-500/30 dark:text-amber-100">
                  Inasistencia: {listStatusSummary.no_show}
                </Badge>
              </>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-0 pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Paciente</TableHead>
                <TableHead>Edad</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Fecha y Hora</TableHead>
                <TableHead>Convenio</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Estado</TableHead>
                {canDeleteAppointments ? (
                  <TableHead className="text-right">Acciones</TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {listFilteredAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canDeleteAppointments ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    {listSearchActive
                      ? 'No se encontraron citas para ese nombre o teléfono'
                      : listFilteredAppointments.length === 0 && appointments.length > 0
                        ? 'No hay citas canceladas, atendidas o con inasistencia en el periodo'
                        : 'No hay citas registradas en el periodo'}
                  </TableCell>
                </TableRow>
              ) : (
                listFilteredAppointments.map((appointment) => (
                  <TableRow
                    key={appointment.id}
                    data-testid={`appointment-row-${appointment.id}`}
                    className={appointmentRowHighlightClass(appointment)}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{appointment.patient_name}</span>
                        </div>
                        {appointment.appointment_type_label ? (
                          <Badge variant="outline" className="w-fit text-xs font-normal">
                            {appointment.appointment_type_label}
                          </Badge>
                        ) : null}
                        {appointment.appointment_kind === 'urgent' ? (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs border-orange-500/50 bg-orange-100/50 text-orange-900 dark:text-orange-100"
                          >
                            Cita urgente
                          </Badge>
                        ) : appointment.appointment_kind === 'review' ? (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs border-violet-500/50 bg-violet-100/50 text-violet-900 dark:text-violet-100"
                          >
                            Revisión breve
                          </Badge>
                        ) : null}
                        {needsAttendanceConfirmation(appointment) ? (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs border-sky-500/50 bg-sky-100/50 text-sky-900 dark:text-sky-100"
                          >
                            Asistencia sin confirmar
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                      {appointment.patient_age != null && appointment.patient_age !== ''
                        ? `${appointment.patient_age} años`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {appointment.patient_email || '—'}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {appointment.patient_phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal whitespace-nowrap">
                        {appointment.booking_source === 'admin'
                          ? 'Panel admin'
                          : appointment.booking_source === 'whatsapp'
                            ? 'WhatsApp'
                            : 'Sitio web'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <CalendarIconLucide className="w-4 h-4 text-primary" />
                          <span>
                            {formatAppointmentDateOnlyInClinic(appointment.date, 'EEE, d MMM yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{appointment.time}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{appointment.convenio_nombre || 'No aplica'}</p>
                        {appointment.convenio_empresa &&
                        appointment.convenio_empresa !== appointment.convenio_nombre ? (
                          <p className="text-xs text-muted-foreground">{appointment.convenio_empresa}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {appointment.reason || 'Sin especificar'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(appointment.status)}
                    </TableCell>
                    {canDeleteAppointments ? (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={deletingAppointmentId === appointment.id}
                          onClick={() => handleDeleteAppointment(appointment)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {deletingAppointmentId === appointment.id ? 'Borrando…' : 'Borrar'}
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={noShowDialogOpen}
        onOpenChange={(open) => {
          setNoShowDialogOpen(open);
          if (!open) setNoShowTarget({ id: null, patientName: '' });
        }}
      >
        <DialogContent className="sm:max-w-md border-amber-200/80 dark:border-amber-800/60">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-amber-900 dark:text-amber-100">
              <UserX className="w-5 h-5 shrink-0" aria-hidden />
              No llegó paciente
            </DialogTitle>
            <DialogDescription>
              Confirma que el paciente no asistió a la cita programada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            {noShowTarget.patientName ? (
              <p>
                Paciente:{' '}
                <span className="font-medium text-foreground">{noShowTarget.patientName}</span>
              </p>
            ) : null}
            <p>
              Se registrará una inasistencia para el teléfono de esta cita. La cita pasará al estado{' '}
              <span className="font-medium text-foreground">No llegó</span> y saldrá del calendario activo; el evento en
              Google Calendar se eliminará si estaba sincronizado.
            </p>
            <p className="rounded-md border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-amber-900/90 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100/90">
              Tras varias inasistencias del mismo número, el sistema puede bloquear nuevas reservas por WhatsApp o web,
              según el límite configurado en Ajustes.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setNoShowDialogOpen(false)}
              disabled={noShowSubmitting}
            >
              Volver
            </Button>
            <Button
              type="button"
              className="bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700"
              onClick={submitMarkNoShow}
              disabled={noShowSubmitting}
            >
              {noShowSubmitting ? 'Guardando…' : 'Confirmar inasistencia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelTarget({ id: null, patientName: '' });
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancelar cita</DialogTitle>
            <DialogDescription>
              Si escribes un mensaje y el paciente tiene teléfono, se enviará por WhatsApp al guardar. Puedes dejar el
              texto vacío para cancelar sin notificación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-wa-message">Mensaje para el paciente (WhatsApp)</Label>
            <Textarea
              id="cancel-wa-message"
              rows={5}
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              className="resize-y min-h-[120px]"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelSubmitting}>
              Volver
            </Button>
            <Button type="button" variant="destructive" onClick={submitCancelAppointment} disabled={cancelSubmitting}>
              {cancelSubmitting ? 'Cancelando…' : 'Cancelar cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClinicalAppointmentDialog
        open={clinicalDialog.open}
        appointmentId={clinicalDialog.appointmentId}
        patientName={clinicalDialog.patientName}
        onOpenChange={(open) => setClinicalDialog((prev) => ({ ...prev, open }))}
      />
    </div>
  );
}
